// The header bell's popover: what the ACCOUNT is told — not what wallets
// traded. The operator, 2026-10-18: "in notifications it should be as before —
// not in design, but in content: no buys and sells, only rewards and so on,
// about the account, referrals".
//
// What it draws is what the data really has (accountNotice.ts): the primary
// wallet's rewards reading — the Rewards page's own GET, asked when the bell
// opens — gives one notice, the GRAM there is to claim and what it is made of;
// a press opens Rewards. Nothing else about the account has a source yet; an
// account with nothing to claim reads "You're all caught up".
//
// The tracked wallets' trades are no longer here. The tracker is untouched —
// its rules in Settings, its live alert toasts — and its inbox list is kept as
// TrackerInbox, for a Tracker view of its own. The foot says where its alerts
// go now, and holds the door to Settings.
//
// The popover is the round-6 one in look: the lab's wallets popover, its
// ground, edge and row; the layer under it unpainted (no dim) and marked
// data-scrim for the page's shortcuts; the body one height in every state, so
// nothing moves when the reading lands. The header owns whether it is open;
// `onClose` closes it behind a notice that is opened, `onOpenSettings` gives
// the foot its door.

import { BellIcon, CaretRightIcon, GearSixIcon, GiftIcon } from '@phosphor-icons/react'
import type { MouseEvent } from 'react'
import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useRewardsSummary } from '../../hooks/useRewardsSummary'
import { useOptionalWallets } from '../../wallet/WalletsContext'
import { figs } from '../ui/fig'
import { GramMark } from '../ui/icons/GramMark'
import { useWalletTrackerOptional } from './WalletTrackerProvider'
import { type AccountNotice, accountNotices } from './accountNotice'
import styles from './notifications.module.css'

export function NotificationsPopover({
  onClose,
  onOpenSettings,
}: {
  /** Closes the popover. With it, opening a notice closes the popover behind it. */
  onClose?: () => void
  /** Opens the Settings dialog — where alerts and tracked wallets are set. */
  onOpenSettings?: () => void
} = {}) {
  const navigate = useNavigate()
  const wallets = useOptionalWallets()
  const tracker = useWalletTrackerOptional()
  // the primary wallet's reading, as the Rewards page and the account menu ask it
  const address = wallets?.active?.address ?? null
  const rewards = useRewardsSummary(address)
  const notices = accountNotices(rewards.summary)

  // The press on the unpainted layer under the popover closes it (the header's
  // outside-press rule) and hands the focus back to the bell it hangs from.
  const popRef = useRef<HTMLElement>(null)
  const returnFocus = (event: MouseEvent) => {
    event.preventDefault()
    const trigger = popRef.current?.previousElementSibling
    if (trigger instanceof HTMLElement) trigger.focus()
  }
  const open = (notice: AccountNotice) => {
    navigate(notice.to)
    onClose?.()
  }

  return (
    <>
      {createPortal(
        <div className={styles.scrim} data-scrim="" aria-hidden="true" onMouseDown={returnFocus} />,
        document.body
      )}
      <section
        className={`${styles.popover} ${styles.account}`}
        ref={popRef}
        aria-label="Notifications"
      >
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <BellIcon size={18} weight="bold" className={styles.headMark} aria-hidden="true" />
            <span className={styles.title}>Notifications</span>
          </div>
        </div>
        <div className={styles.body}>
          {address && rewards.status === 'loading' && !rewards.summary ? (
            <output className={styles.loading} aria-busy="true">
              <span className={styles.srOnly}>Loading account notices…</span>
              <span className={styles.ghost} aria-hidden="true">
                <i className={styles.ghostFace} />
                <span className={styles.ghostText}>
                  <i style={{ width: '62%' }} />
                  <i style={{ width: '40%' }} />
                </span>
              </span>
            </output>
          ) : notices.length > 0 ? (
            <div className={styles.list}>
              <ul className={styles.rows}>
                {notices.map((notice) => (
                  <NoticeRow key={notice.id} notice={notice} onOpen={() => open(notice)} />
                ))}
              </ul>
            </div>
          ) : (
            <Nothing offline={address !== null && rewards.status === 'offline'} />
          )}
        </div>
        <footer className={styles.foot}>
          {/* where the tracked wallets' trades went (when the tracker is on) */}
          <span className={styles.note}>{tracker ? 'Tracked wallets alert as toasts' : ''}</span>
          {onOpenSettings && (
            <span className={styles.footActions}>
              <button
                type="button"
                className={`${styles.manage} ${styles.manageIcon}`}
                aria-label="Settings"
                onClick={onOpenSettings}
              >
                <GearSixIcon size={15} weight="bold" aria-hidden="true" />
              </button>
            </span>
          )}
        </footer>
      </section>
    </>
  )
}

// A notice: its mark, what it is over what it is made of, and the GRAM it is
// about at the right. The whole row is the press (a stretched button under the
// content), as every row of the header's popovers.
function NoticeRow({ notice, onOpen }: { notice: AccountNotice; onOpen: () => void }) {
  return (
    <li className={styles.row} data-kind={notice.id}>
      <button
        type="button"
        className={styles.hit}
        aria-label={`${notice.title}: ${notice.gram} GRAM`}
        onClick={onOpen}
      />
      <span className={styles.accountMark} aria-hidden="true">
        <GiftIcon size={20} weight="bold" />
      </span>
      <span className={styles.text}>
        <span className={styles.rowTitle}>{notice.title}</span>
        {notice.parts.length > 0 && (
          <span className={styles.detail}>
            {notice.parts.map((part, i) => (
              <span key={part.from} className={styles.part}>
                {i > 0 ? ' · ' : ''}
                {part.from} <GramMark size={10} />
                {part.gram}
              </span>
            ))}
          </span>
        )}
      </span>
      <span className={styles.claim}>
        <GramMark size={14} />
        {figs(notice.gram)}
      </span>
      <CaretRightIcon size={12} weight="bold" className={styles.go} aria-hidden="true" />
    </li>
  )
}

// Nothing to say: two lines in the middle of the body, as Search says "No
// results" — or, when the reading did not come, that it did not.
function Nothing({ offline }: { offline: boolean }) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyTitle}>
        {offline ? "Couldn't read your rewards" : "You're all caught up"}
      </span>
      <span className={styles.emptyBody}>
        {offline
          ? 'Notices about your account come back when it answers.'
          : 'Rewards, referrals and account updates will appear here.'}
      </span>
    </div>
  )
}
